/**
 * Postal Code Validation Utilities
 * Location: src/utils/postalCodeValidation.ts
 * 
 * This file contains postal code validation patterns for various countries.
 * For countries not listed, a default 5-digit validation is used.
 */

// Postal code validation patterns by country (ISO 3166-1 alpha-2 codes)
export const POSTAL_CODE_PATTERNS: { [key: string]: RegExp } = {
  // North America
  'US': /^\d{5}(-\d{4})?$/, // 12345 or 12345-6789
  'CA': /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i, // A1A 1A1
  'MX': /^\d{5}$/, // 12345

  // Western Europe
  'GB': /^[A-Z]{1,2}\d{1,2}[A-Z]? ?\d[A-Z]{2}$/i, // SW1A 1AA
  'IE': /^[A-Z]\d{2} ?[A-Z0-9]{4}$/i, // D02 AF30 (Eircode)
  'DE': /^\d{5}$/, // 10115
  'FR': /^\d{5}$/, // 75001
  'ES': /^\d{5}$/, // 28001
  'IT': /^\d{5}$/, // 00100
  'NL': /^\d{4} ?[A-Z]{2}$/i, // 1012 AB
  'BE': /^\d{4}$/, // 1000
  'AT': /^\d{4}$/, // 1010
  'CH': /^\d{4}$/, // 8001
  'LU': /^\d{4}$/, // 1234
  'PT': /^\d{4}-?\d{3}$/, // 1000-001

  // Northern Europe
  'SE': /^\d{3} ?\d{2}$/, // 111 22
  'NO': /^\d{4}$/, // 0001
  'DK': /^\d{4}$/, // 1000
  'FI': /^\d{5}$/, // 00100
  'IS': /^\d{3}$/, // 101

  // Eastern Europe
  'PL': /^\d{2}-?\d{3}$/, // 00-001
  'CZ': /^\d{3} ?\d{2}$/, // 100 00
  'SK': /^\d{3} ?\d{2}$/, // 010 01
  'HU': /^\d{4}$/, // 1011
  'RO': /^\d{6}$/, // 010011
  'BG': /^\d{4}$/, // 1000
  'HR': /^\d{5}$/, // 10000
  'SI': /^\d{4}$/, // 1000
  'EE': /^\d{5}$/, // 10111
  'LV': /^LV-\d{4}$/i, // LV-1010
  'LT': /^LT-\d{5}$/i, // LT-01001

  // Southern Europe
  'GR': /^\d{3} ?\d{2}$/, // 100 01
  'CY': /^\d{4}$/, // 1010
  'MT': /^[A-Z]{3} ?\d{4}$/i, // ABC 1234

  // Asia-Pacific
  'AU': /^\d{4}$/, // 2000
  'NZ': /^\d{4}$/, // 1010
  'JP': /^\d{3}-?\d{4}$/, // 100-0001
  'KR': /^\d{5}$/, // 12345
  'SG': /^\d{6}$/, // 123456
  'MY': /^\d{5}$/, // 50000
  'TH': /^\d{5}$/, // 10100
  'VN': /^\d{6}$/, // 100000
  'PH': /^\d{4}$/, // 1000
  'ID': /^\d{5}$/, // 10110
  'IN': /^\d{6}$/, // 110001
  'PK': /^\d{5}$/, // 44000
  'BD': /^\d{4}$/, // 1000
  'LK': /^\d{5}$/, // 10100
  'CN': /^\d{6}$/, // 100000
  'TW': /^\d{3}(-?\d{2,3})?$/, // 100 or 100-01
  // HK and MO: No postal code system (handled by requiresPostalCode function)

  // Middle East
  'IL': /^\d{5}(\d{2})?$/, // 12345 or 1234567
  'TR': /^\d{5}$/, // 34000
  'SA': /^\d{5}(-?\d{4})?$/, // 12345 or 12345-1234
  // AE and QA: No postal code system (handled by requiresPostalCode function)
  'KW': /^\d{5}$/, // 12345
  'OM': /^\d{3}$/, // 100
  'BH': /^\d{3,4}$/, // 100 or 1000
  'JO': /^\d{5}$/, // 11000
  'LB': /^\d{4}( ?\d{4})?$/, // 1000 or 1000 1000
  'IQ': /^\d{5}$/, // 10001

  // Americas (South & Central)
  'BR': /^\d{5}-?\d{3}$/, // 01310-100
  'AR': /^[A-Z]?\d{4}([A-Z]{3})?$/i, // 1000 or C1000ABC
  'CL': /^\d{7}$/, // 1234567
  'CO': /^\d{6}$/, // 110111
  'PE': /^\d{5}$/, // 15001
  'VE': /^\d{4}(-[A-Z])?$/i, // 1010 or 1010-A
  'EC': /^\d{6}$/, // 170101
  'UY': /^\d{5}$/, // 11000
  'PY': /^\d{4}$/, // 1000
  'BO': /^\d{4}$/, // 1000
  'CR': /^\d{4,5}$/, // 1000 or 10101
  'PA': /^\d{4}$/, // 1000
  'GT': /^\d{5}$/, // 01001
  'SV': /^\d{4}$/, // 1101
  'HN': /^\d{5}$/, // 11101
  'NI': /^\d{5}$/, // 10000
  'DO': /^\d{5}$/, // 10100
  'CU': /^\d{5}$/, // 10100
  'JM': /^[A-Z]{4}\d{2}$/i, // JMAAA01
  'TT': /^\d{6}$/, // 100101

  // Africa
  'ZA': /^\d{4}$/, // 0001
  'EG': /^\d{5}$/, // 11511
  'NG': /^\d{6}$/, // 100001
  'KE': /^\d{5}$/, // 00100
  'GH': /^[A-Z]{2}-?\d{3,4}-?\d{4}$/i, // AB-123-4567
  'TN': /^\d{4}$/, // 1000
  'MA': /^\d{5}$/, // 10000
  'DZ': /^\d{5}$/, // 16000
  'ET': /^\d{4}$/, // 1000
  // UG: No postal code system (handled by requiresPostalCode function)
  'TZ': /^\d{5}$/, // 11000
  'MU': /^\d{5}$/, // 11000
  'SN': /^\d{5}$/, // 10000
};

// Postal code placeholders/examples by country
export const POSTAL_CODE_PLACEHOLDERS: { [key: string]: string } = {
  'US': '12345',
  'CA': 'A1A 1A1',
  'MX': '12345',
  'GB': 'SW1A 1AA',
  'IE': 'D02 AF30',
  'DE': '10115',
  'FR': '75001',
  'ES': '28001',
  'IT': '00100',
  'NL': '1012 AB',
  'BE': '1000',
  'AT': '1010',
  'CH': '8001',
  'LU': '1234',
  'PT': '1000-001',
  'SE': '111 22',
  'NO': '0001',
  'DK': '1000',
  'FI': '00100',
  'IS': '101',
  'PL': '00-001',
  'CZ': '100 00',
  'SK': '010 01',
  'HU': '1011',
  'RO': '010011',
  'BG': '1000',
  'HR': '10000',
  'SI': '1000',
  'EE': '10111',
  'LV': 'LV-1010',
  'LT': 'LT-01001',
  'GR': '100 01',
  'CY': '1010',
  'MT': 'ABC 1234',
  'AU': '2000',
  'NZ': '1010',
  'JP': '100-0001',
  'KR': '12345',
  'SG': '123456',
  'MY': '50000',
  'TH': '10100',
  'VN': '100000',
  'PH': '1000',
  'ID': '10110',
  'IN': '110001',
  'PK': '44000',
  'BD': '1000',
  'LK': '10100',
  'CN': '100000',
  'TW': '100-01',
  'IL': '12345',
  'TR': '34000',
  'SA': '12345',
  'KW': '12345',
  'OM': '100',
  'BH': '1000',
  'JO': '11000',
  'LB': '1000 1000',
  'IQ': '10001',
  'BR': '01310-100',
  'AR': 'C1000ABC',
  'CL': '1234567',
  'CO': '110111',
  'PE': '15001',
  'VE': '1010',
  'EC': '170101',
  'UY': '11000',
  'PY': '1000',
  'BO': '1000',
  'CR': '10101',
  'PA': '1000',
  'GT': '01001',
  'SV': '1101',
  'HN': '11101',
  'NI': '10000',
  'DO': '10100',
  'CU': '10100',
  'JM': 'JMAAA01',
  'TT': '100101',
  'ZA': '0001',
  'EG': '11511',
  'NG': '100001',
  'KE': '00100',
  'GH': 'AB-123-4567',
  'TN': '1000',
  'MA': '10000',
  'DZ': '16000',
  'ET': '1000',
  'TZ': '11000',
  'MU': '11000',
  'SN': '10000',
};

/**
 * Validates a postal code for a given country
 * If no pattern exists for the country, defaults to 5-digit validation
 */
export const validatePostalCode = (code: string, countryCode: string): boolean => {
  const trimmedCode = code.trim();

  // If empty, return false
  if (!trimmedCode) {
    return false;
  }

  const pattern = POSTAL_CODE_PATTERNS[countryCode];

  if (pattern) {
    return pattern.test(trimmedCode);
  }

  // Default: Accept 5 digits for unknown countries
  return /^\d{5}$/.test(trimmedCode);
};

/**
 * Gets the placeholder text for a country's postal code
 * Defaults to "12345" for unknown countries
 */
export const getPostalCodePlaceholder = (countryCode: string): string => {
  return POSTAL_CODE_PLACEHOLDERS[countryCode] || '12345';
};

/**
 * Checks if a country requires a postal code
 * Some countries/regions don't use postal codes
 */
export const requiresPostalCode = (countryCode: string): boolean => {
  const noPostalCodeCountries = ['HK', 'MO', 'AE', 'QA', 'UG'];
  return !noPostalCodeCountries.includes(countryCode);
};